import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Meta (Facebook) Pixel — same pixel ID as cleanno-marketing.
 * Initializes once, tracks PageView on SPA navigations (no duplicate first paint vs snippet).
 */
@Injectable({
  providedIn: 'root',
})
export class MetaPixelService {
  private initialized = false;
  private routerSubscriptionBound = false;

  constructor(
    private router: Router,
    private ngZone: NgZone,
  ) {}

  init(): void {
    const pixelId = environment.metaPixel?.id;
    if (!pixelId || typeof window === 'undefined' || this.initialized) {
      return;
    }
    this.initialized = true;

    if (!window.fbq) {
      const bootstrap = document.createElement('script');
      bootstrap.textContent = `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');`;
      document.head.appendChild(bootstrap);
    }

    this.bindRouterPageViews();
  }

  trackPageView(): void {
    if (!this.canTrack()) {
      return;
    }
    window.fbq!('track', 'PageView');
  }

  /** Standard Meta conversion — call only after Supabase signup succeeds. */
  trackCompleteRegistration(): void {
    if (!this.canTrack()) {
      return;
    }
    window.fbq!('track', 'CompleteRegistration');
  }

  private canTrack(): boolean {
    return Boolean(
      environment.metaPixel?.id &&
        typeof window !== 'undefined' &&
        typeof window.fbq === 'function',
    );
  }

  private bindRouterPageViews(): void {
    if (this.routerSubscriptionBound) {
      return;
    }
    this.routerSubscriptionBound = true;

    let lastUrl: string | null = null;

    this.ngZone.runOutsideAngular(() => {
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          const url = e.urlAfterRedirects;
          if (url === lastUrl) {
            return;
          }
          lastUrl = url;
          this.ngZone.run(() => this.trackPageView());
        });
    });
  }
}
